import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from "@mui/material";
import StopIcon from "@mui/icons-material/Stop";
import { useStatusPalette } from "../../../utils/statusColors";
import useAgentStore from "../useAgentStore";
import { sendManagedRunRequest } from "../ManagedRunPolicy/managedRunApi";

const MANAGED_RUN_KIND = "MANAGED_RUN";
const ACTIVE_SCHEDULED_STATUSES = new Set(["SCHEDULED", "RESUMING"]);
const ACTIVE_RECURRING_STATUSES = new Set(["ACTIVE", "FIRING"]);

const WAKE_REASON_LABELS = {
  HEALTH_CHECK: "Health check",
  SUMMARY: "Summary",
  NO_PROGRESS_DEADLINE: "No-progress deadline",
  CUSTOM_DEADLINE: "Custom deadline",
  WORKFLOW_EVENT: "Workflow event",
  USER_MESSAGE: "User message",
};

function wakeLabel(wakeReason) {
  return WAKE_REASON_LABELS[wakeReason] || "Mission wake";
}

function parseDate(value) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatCountdown(targetDate, nowMs) {
  if (!targetDate) return "not scheduled";
  let remainingMs = targetDate.getTime() - nowMs;
  if (remainingMs <= 0) return "due now";

  const days = Math.floor(remainingMs / 86400000);
  remainingMs -= days * 86400000;
  const hours = Math.floor(remainingMs / 3600000);
  remainingMs -= hours * 3600000;
  const minutes = Math.floor(remainingMs / 60000);
  remainingMs -= minutes * 60000;
  const seconds = Math.floor(remainingMs / 1000);

  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

function formatDuration(value) {
  if (!value || typeof value !== "string") return "";
  const match = value.match(
    /^P(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?)?$/
  );
  if (!match) return value;
  const [, days, hours, minutes, seconds] = match;
  const parts = [];
  if (days) parts.push(`${days}d`);
  if (hours) parts.push(`${hours}h`);
  if (minutes) parts.push(`${minutes}m`);
  if (seconds) parts.push(`${seconds}s`);
  return parts.join(" ") || value;
}

function toManagedRunEvents(scheduled, recurring) {
  const oneOffEvents = (scheduled || [])
    .filter(
      (schedule) =>
        schedule?.scheduleKind === MANAGED_RUN_KIND &&
        ACTIVE_SCHEDULED_STATUSES.has(schedule?.status)
    )
    .map((schedule) => ({
      id: schedule.scheduleId,
      kind: "scheduled",
      label: wakeLabel(schedule.wakeReason),
      dueAt: parseDate(schedule.resumeAt),
    }));

  const recurringEvents = (recurring || [])
    .filter(
      (schedule) =>
        schedule?.scheduleKind === MANAGED_RUN_KIND &&
        ACTIVE_RECURRING_STATUSES.has(schedule?.status)
    )
    .map((schedule) => ({
      id: schedule.scheduleId,
      kind: "recurring",
      label: wakeLabel(schedule.wakeReason),
      dueAt: parseDate(schedule.nextResumeAt),
      interval: formatDuration(schedule.intervalDuration),
    }));

  return [...oneOffEvents, ...recurringEvents]
    .filter((event) => event.dueAt)
    .sort((a, b) => a.dueAt.getTime() - b.dueAt.getTime());
}

export default function ManagedRunOperatorBanner({ session, isActive }) {
  const _sp = useStatusPalette();
  const [scheduled, setScheduled] = useState([]);
  const [recurring, setRecurring] = useState([]);
  const [loading, setLoading] = useState(false);
  const [nowMs, setNowMs] = useState(Date.now());
  const [stopDialogOpen, setStopDialogOpen] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [scheduleError, setScheduleError] = useState("");

  const chatSessionId = session?.chatSessionId;
  const isScheduledManagedRun = session?.managedRun === true;
  const isStoppedManagedRun = session?.managedRunStatus === "STOPPED";

  useEffect(() => {
    if (!chatSessionId || !isActive || !isScheduledManagedRun) {
      setScheduled([]);
      setRecurring([]);
      setScheduleError("");
      return;
    }

    let cancelled = false;
    async function loadSchedules() {
      setLoading(true);
      try {
        const [scheduledResponse, recurringResponse] = await Promise.all([
          sendManagedRunRequest(
            `project-service/agent/session/${chatSessionId}/scheduled-messages`,
            "",
            {},
            "GET"
          ),
          sendManagedRunRequest(
            `project-service/agent/session/${chatSessionId}/recurring-schedules`,
            "",
            {},
            "GET"
          ),
        ]);
        if (!cancelled) {
          setScheduled(scheduledResponse?.data || []);
          setRecurring(recurringResponse?.data || []);
          setScheduleError("");
        }
      } catch (error) {
        if (!cancelled) {
          setScheduled([]);
          setRecurring([]);
          setScheduleError(
            error?.response?.data?.message ||
              error?.message ||
              "Unable to load mission schedules"
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadSchedules();
    const refreshId = window.setInterval(loadSchedules, 60000);
    return () => {
      cancelled = true;
      window.clearInterval(refreshId);
    };
  }, [chatSessionId, isActive, isScheduledManagedRun]);

  useEffect(() => {
    const tickId = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(tickId);
  }, []);

  const events = useMemo(
    () => toManagedRunEvents(scheduled, recurring),
    [scheduled, recurring]
  );
  const nextEvent = events[0];

  const handleStop = async () => {
    if (!chatSessionId || stopping) return;
    setStopping(true);
    try {
      await useAgentStore
        .getState()
        .stopManagedRun(
          chatSessionId,
          "Stopped duplicate mission operator"
        );
      setStopDialogOpen(false);
    } finally {
      setStopping(false);
    }
  };

  return (
    <>
      <Box
        sx={{
          px: 2.5,
          py: 1,
          borderBottom: 1,
          borderColor: isActive ? _sp.amberAlt.border : "divider",
          backgroundColor: isActive ? _sp.amberAlt.bg : "action.hover",
          display: "flex",
          alignItems: "center",
          gap: 1,
          flexShrink: 0,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: "12px",
              fontWeight: 600,
              color: isActive ? _sp.amberAlt.color : "text.secondary",
            }}
          >
            {isActive
              ? "Mission operator"
              : isStoppedManagedRun
              ? "Mission management stopped"
              : "Operator chat only"}
          </Typography>
          {isActive ? (
            <Typography
              sx={{
                fontSize: "11px",
                color: "text.secondary",
                mt: 0.25,
              }}
            >
              {scheduleError
                ? scheduleError
                : loading && events.length === 0
                ? "Loading schedule..."
                : nextEvent
                ? `Next: ${nextEvent.label} in ${formatCountdown(
                    nextEvent.dueAt,
                    nowMs
                  )}`
                : "No active schedules"}
              {!scheduleError && events.length > 0
                ? ` · ${events
                    .slice(0, 3)
                    .map((event) =>
                      event.kind === "recurring" && event.interval
                        ? `${event.label} every ${event.interval}`
                        : `${event.label} in ${formatCountdown(
                            event.dueAt,
                            nowMs
                          )}`
                    )
                    .join(" · ")}`
                : ""}
            </Typography>
          ) : !isStoppedManagedRun ? (
            <Typography
              sx={{ fontSize: "11px", color: "text.secondary", mt: 0.25 }}
            >
              No schedules attached.
            </Typography>
          ) : null}
        </Box>
        {isActive && isScheduledManagedRun && (
          <Button
            size="small"
            color="error"
            variant="outlined"
            startIcon={<StopIcon />}
            disabled={stopping}
            onClick={() => setStopDialogOpen(true)}
          >
            Stop this chat
          </Button>
        )}
      </Box>

      <Dialog
        open={stopDialogOpen}
        onClose={stopping ? undefined : () => setStopDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Stop this operator?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Only this chat stops. Other mission chats stay active.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStopDialogOpen(false)} disabled={stopping}>
            Cancel
          </Button>
          <Button color="error" onClick={handleStop} disabled={stopping}>
            Stop
            {stopping && <CircularProgress size={16} sx={{ ml: 1 }} />}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
